import { Link } from 'react-router-dom';
import { ArrowRight, ShieldCheck, Zap } from 'lucide-react';
import { useScrollReveal } from '../../hooks/useScrollReveal';

export default function CreditScoreSection() {
  const section = useScrollReveal<HTMLDivElement>({ threshold: 0.15 });

  return (
    <section className="relative overflow-hidden py-16 md:py-24 bg-gray-900">
      {/* Background layers */}
      <div className="absolute inset-0 bg-gradient-to-br from-gray-900 via-gray-800 to-primary/30" />
      <div className="absolute top-0 left-0 w-[400px] h-[400px] bg-primary/15 rounded-full blur-3xl" />
      <div className="absolute bottom-0 right-0 w-[350px] h-[350px] bg-emerald-500/10 rounded-full blur-3xl" />
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-primary/5 rounded-full blur-3xl" />
      {/* Dot grid */}
      <div className="absolute inset-0 opacity-[0.03]" style={{ backgroundImage: 'radial-gradient(circle, #fff 1px, transparent 1px)', backgroundSize: '24px 24px' }} />

      <div ref={section.ref} className="section-container relative px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-5 gap-10 lg:gap-16 items-center">
          {/* Left — text & CTA (3 cols) */}
          <div className={`lg:col-span-3 reveal-left ${section.isVisible ? 'visible' : ''}`}>
            <div className="inline-flex items-center gap-2 bg-primary/20 border border-primary/30 rounded-full px-4 py-1.5 mb-6 text-sm font-medium text-primary">
              <Zap size={14} />
              Takes Just 30 Seconds
            </div>
            <h2 className="text-2xl md:text-3xl lg:text-4xl font-bold text-white tracking-tight mb-4 leading-tight">
              Get Your Free Instant Credit Score
            </h2>
            <p className="text-lg text-gray-300 mb-8 max-w-lg leading-relaxed">
              Know exactly where you stand before you invest. Our free, instant credit check gives you clarity &mdash; no obligations, no impact on your score.
            </p>
            <Link
              to="/tools/credit-check"
              className="inline-flex items-center gap-2 bg-primary hover:bg-primary-dark text-white font-semibold px-8 py-3.5 rounded-lg transition-all shadow-lg shadow-primary/25 hover:shadow-xl hover:-translate-y-0.5 group"
            >
              Get Your Free Credit Check
              <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform" />
            </Link>
            <div className="flex items-center gap-4 mt-6 text-sm text-gray-400">
              <span className="flex items-center gap-1.5"><ShieldCheck size={14} className="text-primary" /> No impact on credit</span>
              <span className="text-gray-700">|</span>
              <span className="flex items-center gap-1.5"><Zap size={14} className="text-primary" /> Instant results</span>
            </div>
          </div>

          {/* Right — visual element (2 cols) */}
          <div className={`hidden lg:flex lg:col-span-2 justify-center reveal-right ${section.isVisible ? 'visible' : ''}`}>
            <div className="relative">
              {/* Glow */}
              <div className="absolute inset-0 bg-primary/20 rounded-full blur-3xl scale-125" />

              {/* Credit score gauge card */}
              <div className="w-56 h-72 rounded-2xl border border-white/10 bg-gradient-to-b from-gray-800/80 to-gray-900/80 backdrop-blur-sm relative overflow-hidden shadow-2xl p-6 flex flex-col items-center justify-center">
                {/* Outer ring */}
                <div className="relative w-36 h-36 mb-4">
                  <svg className="w-full h-full -rotate-90" viewBox="0 0 120 120">
                    <circle cx="60" cy="60" r="52" fill="none" stroke="rgba(255,255,255,0.05)" strokeWidth="8" />
                    <circle
                      cx="60" cy="60" r="52" fill="none"
                      stroke="url(#scoreGradient)" strokeWidth="8" strokeLinecap="round"
                      strokeDasharray="262" strokeDashoffset="52"
                    />
                    <defs>
                      <linearGradient id="scoreGradient" x1="0%" y1="0%" x2="100%" y2="0%">
                        <stop offset="0%" stopColor="#3d7a24" />
                        <stop offset="100%" stopColor="#34d399" />
                      </linearGradient>
                    </defs>
                  </svg>
                  <div className="absolute inset-0 flex flex-col items-center justify-center">
                    <span className="text-3xl font-bold text-white">780</span>
                    <span className="text-[10px] text-gray-400 font-medium uppercase tracking-wider">Excellent</span>
                  </div>
                </div>
                {/* Mini stats */}
                <div className="flex gap-4 text-center">
                  <div>
                    <div className="h-1.5 w-8 bg-primary rounded-full mx-auto mb-1" />
                    <span className="text-[9px] text-gray-500">Score</span>
                  </div>
                  <div>
                    <div className="h-1.5 w-8 bg-emerald-400 rounded-full mx-auto mb-1" />
                    <span className="text-[9px] text-gray-500">History</span>
                  </div>
                  <div>
                    <div className="h-1.5 w-8 bg-yellow-400 rounded-full mx-auto mb-1" />
                    <span className="text-[9px] text-gray-500">Report</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
